import { useEffect } from 'react';
import { X, Info, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useToast, type ToastKind } from '../hooks/useToast';
import { cn } from '../lib/cn';

const STYLES: Record<ToastKind, { wrap: string; icon: typeof Info }> = {
  info: {
    wrap: 'bg-status-info/15 text-status-info border-status-info/30',
    icon: Info,
  },
  success: {
    wrap: 'bg-status-healthy/15 text-status-healthy border-status-healthy/30',
    icon: CheckCircle2,
  },
  error: {
    wrap: 'bg-status-critical/15 text-status-critical border-status-critical/30',
    icon: AlertTriangle,
  },
};

export const Toaster = () => {
  const { toasts, dismiss } = useToast();

  useEffect(() => {
    if (toasts.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const last = toasts.at(-1);
        if (last) dismiss(last.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toasts, dismiss]);

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      className="pointer-events-none fixed bottom-20 right-4 z-50 flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2 md:bottom-4"
    >
      {toasts.map((t) => {
        const { wrap, icon: Icon } = STYLES[t.kind];
        return (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2 rounded-lg border bg-bg-primary px-3 py-2 text-sm shadow-md',
              wrap,
            )}
          >
            <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1">{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="grid h-5 w-5 shrink-0 place-items-center rounded hover:bg-black/5"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
